import { useRef, useState } from 'react';
import { useAgentStoreContext } from './useLocalState';
import { createChartThumbnail } from '../utils';

interface UseDragChartOptions<S = string> {
  dataId: S;
  chartTitle?: S;
  needThumbnail?: boolean;
}

/**
 * 图表拖拽自定义 Hook
 * @param options - 配置选项
 * @returns 拖拽相关的状态和事件处理函数
 */
export const useDragChart = (options: UseDragChartOptions) => {
  const { dataId, needThumbnail } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const customDragImageRef = useRef<HTMLDivElement | null>(null);
  const { getItem } = useAgentStoreContext();

  // 拖拽开始处理函数
  const handleDragStart = (e: React.DragEvent) => {
    if (!needThumbnail) return;
    setIsDragging(true);

    // 创建自定义拖拽缩略图
    if (chartRef.current) {
      customDragImageRef.current = createChartThumbnail(
        chartRef.current,
        e.clientX,
        e.clientY,
        dataId,
        getItem,
      );
    }

    // 使用透明图片作为原生拖拽图像
    const transparentImg = new Image();
    transparentImg.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(transparentImg, 0, 0);

    // 存储数据
    e.dataTransfer.setData('text/plain', 'chart');
    e.dataTransfer.effectAllowed = 'move';
  };

  // 拖拽过程中更新预览位置
  const handleDrag = (e: React.DragEvent) => {
    if (!needThumbnail) return;
    if (customDragImageRef.current && e.clientX !== 0 && e.clientY !== 0) {
      customDragImageRef.current.style.left = `${e.clientX + 10}px`;
      customDragImageRef.current.style.top = `${e.clientY + 10}px`;
    }
  };

  // 拖拽结束处理函数
  const handleDragEnd = () => {
    setIsDragging(false);

    // 移除自定义预览
    if (customDragImageRef.current) {
      document.body.removeChild(customDragImageRef.current);
      customDragImageRef.current = null;
    }
  };

  // 拖拽经过处理函数
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // 放置处理函数
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // 鼠标移入移出处理
  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  return {
    // 状态
    isDragging,
    isHovering,
    chartRef,

    // 事件处理函数
    dragHandlers: {
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },

    // 鼠标事件处理函数
    hoverHandlers: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
  };
};
